/**
 * Shared ordering / stage-filtering helpers for review groups and questions.
 *
 * A review stores a frozen snapshot of the questionnaire in `result.event`, so
 * these values can be missing, zero or duplicated on older reviews. The naive
 * `if (a.order && b.order) return a.order - b.order; return 0;` comparator
 * silently treated those as "equal", which left questions in whatever sequence
 * the snapshot happened to hold. These helpers always produce one deterministic
 * order.
 */

const toOrder = (item) => {
  const value = Number(item?.order);
  // Missing / non-numeric / 0 all sort last rather than being treated as ties.
  return Number.isFinite(value) && value > 0 ? value : Number.MAX_SAFE_INTEGER;
};

const toId = (item) => {
  const value = Number(item?.id);
  return Number.isFinite(value) ? value : Number.MAX_SAFE_INTEGER;
};

/**
 * Sort by `order`, breaking ties on `id` so equal or missing order values
 * never render in a different sequence between two visits to the same page.
 */
export function sortByOrder(items) {
  if (!Array.isArray(items)) return [];
  return [...items].sort((a, b) => {
    const diff = toOrder(a) - toOrder(b);
    return diff !== 0 ? diff : toId(a) - toId(b);
  });
}

/**
 * Normalise a follow-up interval to a number, or null for "initial review".
 */
export function normaliseInterval(value) {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : null;
}

/**
 * Pick the groups that belong to a given review stage.
 *
 * - initial review  -> groups with no follow_up_months
 * - 6/12/... month  -> groups tagged with that exact interval
 *
 * Both sides are coerced to numbers first, so a snapshot holding "6" as a
 * string still matches a review with interval_months 6.
 */
export function groupsForStage(groups, reviewType, intervalMonths) {
  if (!Array.isArray(groups)) return [];

  const interval = reviewType === 'followup' ? normaliseInterval(intervalMonths) : null;

  return groups.filter((group) => normaliseInterval(group?.follow_up_months) === interval);
}
